import React, { useMemo, useRef } from 'react';
import { Input, Modal, Skeleton, Upload, Typography, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import {
  compressImage,
  isSupportedImage,
  SUPPORTED_IMAGE_ACCEPT,
  SUPPORTED_IMAGE_LABEL,
  UnsupportedImageError,
} from '../../utils/imageCompression';
import { stampLocationTime, formatOverlayDateTime } from '../../utils/imageUtils';
import { getCurrentLocation } from '../../utils/locationUtils';

// A camera capture is brand new; a gallery pick carries the photo's original
// (older) timestamp. Only fresh captures get the location/time stamp.
const CAPTURE_MAX_AGE_MS = 2 * 60 * 1000;

const isFreshCapture = (file) => {
  const lastModified = Number(file?.lastModified);
  if (!Number.isFinite(lastModified) || lastModified <= 0) return true; // no timestamp → assume camera
  return Date.now() - lastModified <= CAPTURE_MAX_AGE_MS;
};

const { Text } = Typography;

/**
 * Comment + photo evidence for one rated question.
 */
const SAVED_PREFIX = 'saved:';

const savedImageId = (uid) =>
  String(uid).startsWith(SAVED_PREFIX) ? String(uid).slice(SAVED_PREFIX.length) : null;

const FieldEvidence = ({
  savedImages = [],
  evidence,
  onChange,
  onRemoveSaved,
  evidenceRequired = false,
  photoRequired = false,
  commentRequired = false,
  showImages = true,
  showComment = true,
  disabled = false,
}) => {
  const held = evidence ?? { comment: '', files: [] };
  const compressedUidsRef = useRef(new Set());

  const fileList = useMemo(() => {
    const saved = savedImages.map((image) => ({
      uid: `${SAVED_PREFIX}${image.imageId}`,
      name: image.imageId,
      status: 'done',
      url: image.url,
    }));

    return [...saved, ...(held.files ?? [])];
  }, [savedImages, held.files]);

  const updateFile = (uid, changes) =>
    onChange?.((previous) => ({
      ...previous,
      files: (previous.files ?? []).map((item) =>
        item.uid === uid ? { ...item, ...changes } : item
      ),
    }));

  const dropFile = (uid) =>
    onChange?.((previous) => {
      const going = (previous.files ?? []).find((item) => item.uid === uid);
      if (going?.thumbUrl?.startsWith('blob:')) URL.revokeObjectURL(going.thumbUrl);

      return { ...previous, files: (previous.files ?? []).filter((item) => item.uid !== uid) };
    });

  const handleChange = ({ fileList: next }) => {
    // Saved photos are owned by the server, not by this list -- keep only the
    // staged ones so they are never duplicated back into local state.
    const staged = next.filter((file) => !savedImageId(file.uid));

    const pending = staged.filter(
      (file) => file.originFileObj && !compressedUidsRef.current.has(file.uid)
    );
    const pendingUids = new Set(pending.map((file) => file.uid));

    onChange?.((previous) => ({
      ...previous,
      files: staged.map((file) =>
        pendingUids.has(file.uid) ? { ...file, status: 'uploading', percent: 0 } : file
      ),
    }));

    // Only fresh camera captures are stamped; gallery picks are left untouched.
    // Fetch the location once per batch, at capture time, so each stamp reflects
    // when/where the photo was actually taken.
    const hasFreshCapture = pending.some((file) => isFreshCapture(file.originFileObj));
    const batchLocation = hasFreshCapture ? getCurrentLocation().catch(() => null) : null;
    const batchDateTime = formatOverlayDateTime();

    pending.forEach((file) => {
      compressedUidsRef.current.add(file.uid);
      const shouldStamp = isFreshCapture(file.originFileObj);

      compressImage(file.originFileObj, {
        onProgress: (percent) => updateFile(file.uid, { percent }),
      })
        .then((compressed) => {
          // antd tracks the file by uid, so the compressed replacement has to
          // carry the original's.
          compressed.uid = file.uid;

          // Finalise with the compressed image right away so the draft save and
          // preview never wait on the location lookup (which can take seconds).
          updateFile(file.uid, {
            originFileObj: compressed,
            size: compressed.size,
            status: 'done',
            percent: 100,
            thumbUrl: URL.createObjectURL(compressed),
          });

          // Gallery picks keep the plain compressed image — no stamp.
          if (!shouldStamp) return;

          // Burn the location + timestamp on in the background, then swap the
          // tagged image in. Failures just leave the compressed image in place.
          Promise.resolve(batchLocation)
            .then((loc) =>
              stampLocationTime(compressed, {
                latitude: loc?.latitude,
                longitude: loc?.longitude,
                dateTime: batchDateTime,
              })
            )
            .then((stamped) => {
              if (!stamped || stamped === compressed) return;
              stamped.uid = file.uid;
              updateFile(file.uid, {
                originFileObj: stamped,
                size: stamped.size,
                thumbUrl: URL.createObjectURL(stamped),
              });
            })
            .catch((error) => console.warn('Location/time stamp failed:', error));
        })
        .catch((error) => {
          if (error instanceof UnsupportedImageError) {
            dropFile(file.uid);
            message.error(error.message);
            return;
          }

          // If compression fails, keep the original.
          updateFile(file.uid, { status: 'done', percent: 100 });
        });
    });
  };

  const handleRemove = (file) => {
    const imageId = savedImageId(file.uid);

    if (!imageId) return true;

    Modal.confirm({
      title: 'Remove this photo?',
      content: 'It will be deleted from the document straight away.',
      okText: 'Remove',
      okButtonProps: { danger: true },
      onOk: () => onRemoveSaved?.(imageId),
    });

    return false;
  };

  const needsComment = showComment && (evidenceRequired || commentRequired);
  const needsPhoto = showImages && (evidenceRequired || photoRequired);

  const missingComment = needsComment && !held.comment?.trim();
  const missingPhoto = needsPhoto && fileList.length === 0;

  return (
    <div className="field-evidence">
      {(missingComment || missingPhoto) && (
        <Text type="danger" className="field-evidence__notice">
          {missingComment && missingPhoto
            ? 'This answer needs a comment and a photo before the document can be completed.'
            : missingComment
              ? 'This answer needs a comment before the document can be completed.'
              : 'This answer needs a photo before the document can be completed.'}
        </Text>
      )}

      {showComment && (
        <div
          className={`field-evidence__block${
            missingComment ? ' field-evidence__block--invalid' : ''
          }`}
        >
          <Text type="secondary" className="field-evidence__label">
            Comments
          </Text>
          <Input.TextArea
            rows={2}
            value={held.comment}
            disabled={disabled}
            placeholder={needsComment ? 'Describe the problem' : 'Optional'}
            onChange={(event) => {
              const { value } = event.target;
              onChange?.((previous) => ({ ...previous, comment: value }));
            }}
          />
        </div>
      )}

      {showImages && (
      <div className="field-evidence__block">
        <Text type="secondary" className="field-evidence__label">
          Photos
        </Text>

        <div
          className={`field-evidence__photos${
            missingPhoto ? ' field-evidence__photos--invalid' : ''
          }`}
        >
          <Upload
            listType="picture-card"
            accept={SUPPORTED_IMAGE_ACCEPT}
            multiple
            disabled={disabled}
            fileList={fileList}
            onChange={handleChange}
            onRemove={handleRemove}
            showUploadList={{ showRemoveIcon: !disabled }}
            beforeUpload={(file) => {
              // Never auto-upload: photos ride inside the document save.
              if (isSupportedImage(file)) return false;

              message.error(
                `${file.name} is not a format browsers can display. ` +
                  `Please upload a ${SUPPORTED_IMAGE_LABEL} file.`
              );

              return Upload.LIST_IGNORE;
            }}
            iconRender={(file) =>
              file.status === 'uploading' ? (
                <Skeleton.Node active className="field-evidence__skeleton" />
              ) : null
            }
          >
            {disabled ? null : <PlusOutlined />}
          </Upload>
        </div>
      </div>
      )}
    </div>
  );
};

export default FieldEvidence;
